import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Heart, Calendar, Users, Baby, CheckCircle, ChevronLeft, ChevronRight, FileCheck, GraduationCap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { pregnancyWeeks, pregnancyArticles, medicalAdvisors, homepageCourses } from '../mockData';

const PREGNANCY_CATEGORIES = [
  'Tất cả',
  'Theo tuần thai',
  'Cơ thể bạn',
  'Bé yêu',
  'Dinh dưỡng & Thể dục',
  'Sức khỏe & An toàn',
  'Cuộc sống',
  'Chuẩn bị cho bé',
  'Quan hệ gia đình'
];

const HomePage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, currentUser } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('Tất cả');
  const weekCarouselRef = useRef(null);
  const articlesCarouselRef = useRef(null);

  const userWeek = currentUser?.currentWeek ?? 12;
  const currentWeekData = pregnancyWeeks.find(w => w.week === userWeek) || pregnancyWeeks[1];
  const displayName = currentUser?.name?.split(' ').pop() || 'Bạn';

  const handleFeatureClick = useCallback((link, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation?.();
    }
    
    // If user clicks on "Theo dõi thai kỳ" or "Lịch hẹn" and not logged in, navigate to that page.
    // The target page will show the Register Popup itself.
    if (!isLoggedIn && (link === '/pregnancy-tracker' || link === '/appointments')) {
      navigate(link);
      return;
    }
    // For other cases (community or logged in users), navigate normally
    navigate(link);
  }, [isLoggedIn, navigate]);

  const handleHeroButtonClick = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Always navigate to pregnancy tracker; the page will handle showing Register Popup if needed
    navigate('/pregnancy-tracker');
  }, [navigate]);

  const features = [
    {
      icon: <Baby className="w-8 h-8 text-pink-500" />,
      title: 'Theo dõi thai kỳ',
      description: 'Cập nhật thông tin thai nhi theo tuần với hình ảnh và lời khuyên chi tiết',
      link: '/pregnancy-tracker'
    },
    {
      icon: <Calendar className="w-8 h-8 text-pink-500" />,
      title: 'Đặt lịch hẹn',
      description: 'Sắp xếp lịch khám thai, tư vấn với bác sĩ dễ dàng',
      link: '/appointments'
    },
    {
      icon: <Users className="w-8 h-8 text-pink-500" />,
      title: 'Cộng đồng mẹ bầu',
      description: 'Kết nối, chia sẻ kinh nghiệm với hàng nghìn mẹ bầu Việt',
      link: '/community'
    }
  ];

  const stats = [
    { number: '50,000+', label: 'Mẹ bầu tin dùng' },
    { number: '100+', label: 'Bác sĩ chuyên khoa' },
    { number: '1,000+', label: 'Bài viết hữu ích' },
    { number: '24/7', label: 'Hỗ trợ trực tuyến' }
  ];

  // Trang chủ khi đã đăng nhập (giao diện theo ảnh, tone hồng + footer)
  if (isLoggedIn) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Banner: AN NHI là đối tác đồng hành của bạn */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 mb-8">
        <section className="bg-gradient-to-r from-pink-50 to-pink-100/80 rounded-2xl py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
              AN NHI là đối tác đồng hành của bạn.
            </h2>
            <div className="flex flex-wrap gap-4 md:gap-6">
              <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 border border-pink-100">
                <Users className="w-8 h-8 text-pink-500" />
                <div>
                  <p className="font-bold text-gray-800">Môi trường</p>
                  <p className="text-sm text-gray-600">thân thiện</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 border border-pink-100">
                <FileCheck className="w-8 h-8 text-pink-500" />
                <div>
                  <p className="font-bold text-gray-800">Cung cấp</p>
                  <p className="text-sm text-gray-600">chatbot AI hỗ trợ 24/7</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white/70 rounded-xl px-4 py-3 border border-pink-100">
                <Heart className="w-8 h-8 text-pink-500" />
                <div>
                  <p className="font-bold text-gray-800">Cung cấp</p>
                  <p className="text-sm text-gray-600">bài viết hữu ích</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          {/* Khối thai kỳ: tuần hiện tại + nội dung tuần */}
          <div className="flex flex-col lg:flex-row gap-6 mb-12">
            <div className="flex-shrink-0">
              <div className="bg-white rounded-xl border border-pink-100 shadow-sm p-4 flex items-center gap-3 w-fit">
                <Baby className="w-8 h-8 text-pink-500" />
                <div>
                  <p className="font-semibold text-gray-800">{displayName}: {userWeek} tuần</p>
                  <p className="text-xs text-gray-500">Chọn tuần khác</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <div className="flex-1 grid md:grid-cols-2 gap-6 bg-white rounded-2xl border border-pink-100 shadow-sm overflow-hidden">
              <div className="relative h-64 md:h-80">
                <img
                  src={currentWeekData.image}
                  alt={currentWeekData.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 rounded-lg px-4 py-2 text-center">
                  <span className="font-semibold text-gray-800">{userWeek} tuần thai</span>
                </div>
                
              </div>
              <div className="p-6 flex flex-col justify-center">
                <p className="text-sm text-gray-500 mb-1">{new Date().toLocaleDateString('vi-VN')}</p>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{currentWeekData.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{currentWeekData.description}</p>
                <Button
                  onClick={() => navigate('/pregnancy-tracker')}
                  variant="outline"
                  className="w-fit border-pink-300 text-pink-600 hover:bg-pink-50"
                >
                  Đọc thêm về tuần của bạn
                </Button>
              </div>
            </div>
          </div>

          {/* Thai kỳ của tôi theo tuần - horizontal scroll */}
          <section className="mb-12">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Thai kỳ của tôi theo tuần</h3>
            <div ref={weekCarouselRef} className="flex gap-3 overflow-x-auto pb-2 scroll-smooth scrollbar-thin">
              {pregnancyWeeks.slice(0, 9).map((w) => (
                <button
                  key={w.week}
                  onClick={() => navigate('/pregnancy-tracker')}
                  className="flex-shrink-0 w-36 rounded-xl border-2 border-pink-200 bg-white p-4 text-left hover:border-pink-400 hover:bg-pink-50 transition-colors"
                >
                  <p className="font-semibold text-gray-800">{w.week} tuần thai</p>
                  <p className="text-xs text-gray-500 mt-1">{w.size}</p>
                </button>
              ))}
            </div>
            <div className="flex justify-center gap-2 mt-2">
              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => weekCarouselRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" className="rounded-full" onClick={() => weekCarouselRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </section>

          {/* Nội dung Thai kỳ: categories + carousel bài viết */}
          <section className="mb-12">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Thai kỳ</h3>
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
              {PREGNANCY_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-pink-500 text-white'
                      : 'bg-white border border-pink-200 text-pink-600 hover:bg-pink-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div ref={articlesCarouselRef} className="flex gap-4 overflow-x-auto pb-2 scroll-smooth">
              {pregnancyArticles.map((art) => (
                <Card
                  key={art.id}
                  className="flex-shrink-0 w-64 overflow-hidden border-pink-100 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate('/pregnancy-tracker')}
                >
                  <img src={art.image} alt={art.title} className="w-full h-36 object-cover" />
                  <div className="p-3 border-b border-pink-100">
                    <p className="font-semibold text-gray-800 line-clamp-2">{art.title}</p>
                    <p className="text-xs text-gray-500 mt-1">Duyệt bởi {art.reviewer}</p>
                  </div>
                </Card>
              ))}
            </div>
            <div className="flex justify-center mt-4">
              <Button
                variant="outline"
                className="border-pink-300 text-pink-600 hover:bg-pink-50"
                onClick={() => navigate('/pregnancy-tracker')}
              >
                Xem tất cả
              </Button>
            </div>
          </section>

          {/* Hội đồng tư vấn y khoa */}
          <section className="mb-12">
            <h3 className="text-xl font-bold text-gray-800 text-center mb-2">Hội đồng tư vấn y khoa</h3>
            <p className="text-gray-600 text-center text-sm mb-6 max-w-2xl mx-auto">
              Gặp gỡ các cố vấn y khoa — chuyên gia uy tín đảm bảo nội dung của chúng tôi đầy đủ và chính xác.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {medicalAdvisors.map((advisor) => (
                <div key={advisor.id} className="flex flex-col items-center text-center p-4 rounded-xl bg-pink-50/50 border border-pink-100">
                  <img src={advisor.avatar} alt={advisor.name} className="w-16 h-16 rounded-full object-cover mb-2" />
                  <p className="font-semibold text-gray-800">{advisor.name}</p>
                  <p className="text-sm text-gray-600">{advisor.specialty}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4">
              <Button variant="outline" className="border-pink-300 text-pink-600 hover:bg-pink-50">
                Xem tất cả
              </Button>
            </div>
          </section>

          {/* Khóa học AN NHI */}
          <section className="bg-gradient-to-br from-pink-50 to-pink-100/50 rounded-2xl p-8 border border-pink-100">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-200 mb-3">
                <GraduationCap className="w-8 h-8 text-pink-600" />
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold text-gray-800">Khóa học AN NHI</h3>
                <span className="px-2 py-0.5 rounded bg-amber-200 text-amber-800 text-xs font-medium">PREMIUM</span>
              </div>
              <p className="text-gray-600 text-sm mt-1">Định hướng các chủ đề khó với chuyên gia nhi khoa hàng đầu.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-6">
              {homepageCourses.map((course) => (
                <Card key={course.id} className="overflow-hidden border-pink-100 bg-white">
                  <img src={course.image} alt={course.title} className="w-full h-40 object-cover" />
                  <div className="p-4">
                    <p className="font-semibold text-gray-800 line-clamp-2 mb-3">{course.title}</p>
                    <Button
                      size="sm"
                      className="w-full bg-pink-500 hover:bg-pink-600 text-white"
                      onClick={() => navigate('/pregnancy-tracker')}
                    >
                      Tìm hiểu thêm
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
            <div className="flex justify-center">
              <Button variant="outline" className="border-pink-300 text-pink-600 hover:bg-pink-50 bg-white">
                Xem thêm
              </Button>
            </div>
          </section>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-pink-50 via-white to-pink-50 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(236,72,153,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(236,72,153,0.05),transparent_50%)]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-in slide-in-from-left duration-700">
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">
                  Đồng hành
                </span>
                <br />
                <span className="text-gray-800">cùng mẹ bầu Việt</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Nơi cung cấp thông tin thai kỳ tin cậy, kết nối cộng đồng mẹ bầu và hỗ trợ chuyên sâu từ các chuyên gia.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  onClick={handleHeroButtonClick}
                  className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  Bắt đầu theo dõi
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/community')}
                  className="border-2 border-pink-300 text-pink-600 hover:bg-pink-50 px-8 py-6 text-lg transition-all duration-300"
                >
                  Khám phá cộng đồng
                </Button>
              </div>
            </div>
            <div className="relative animate-in slide-in-from-right duration-700">
              <div className="absolute -inset-4 bg-gradient-to-r from-pink-200 to-pink-300 rounded-3xl opacity-20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1457342813143-a1ae27448a82?w=800"
                alt="Mẹ bầu hạnh phúc"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center p-6 rounded-2xl bg-gradient-to-br from-pink-50 to-white shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="text-4xl font-bold bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-white to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-4">
              Tính năng nổi bật
            </h2>
            <p className="text-2xl text-gray-600">
              Mọi thứ bạn cần cho hành trình mang thai
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const handleCardAction = (e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                navigate(feature.link);
              };

              return (
                <Card
                  key={index}
                  className="p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-pink-100 bg-white"
                >
                  <button
                    type="button"
                    onClick={handleCardAction}
                    className="w-full text-left focus:outline-none focus:ring-2 focus:ring-pink-500 rounded-lg cursor-pointer"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl flex items-center justify-center mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </button>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-pink-200 to-pink-300 rounded-3xl opacity-20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1583710457367-47de0ea21fef?w=800"
                alt="Mẹ và bé"
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-800">
                Tại sao chọn AN NHI?
              </h2>
              <div className="space-y-4">
                {[
                  'Thông tin được kiểm duyệt bởi bác sĩ chuyên khoa',
                  'Cộng đồng mẹ bầu Việt đông đảo và tích cực',
                  'Công cụ theo dõi thai kỳ chi tiết và dễ sử dụng',
                  'Hỗ trợ trực tuyến nhanh chóng và miễn phí',
                  'Nhắc nhở thông minh cho các cốt mốc quan trọng'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-gray-700 text-lg">{item}</p>
                  </div>
                ))}
              </div>
              <Button 
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white px-8 py-6 text-lg mt-6"
              >
                Tham gia ngay
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-pink-500 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Heart className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Sẵn sàng bắt đầu hành trình?
          </h2>
          <p className="text-xl text-pink-100 mb-8">
            Tham gia cùng hàng nghìn mẹ bầu Việt Nam ngay hôm nay
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-pink-600 hover:bg-pink-50 px-12 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
          >
            Đăng ký miễn phí
          </Button>
        </div>
      </section>

      <Footer />

      {/* Register Popup removed from HomePage; handled by target pages */}
    </div>
  );
};

export default HomePage;